import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { addCountries } from "../../../reducer";
import { InspectorShell } from "./InspectorShell";
import ManageDrawer from "./ManageDrawer";
import BanConfirmDialog from "../../../components/Main/auditorAndArtiste/BanConfirmDialog";
import BookingContactList from "../../../components/Main/auditorAndArtiste/bookingContactList";
import ContactDemand from "../../../components/Main/auditorAndArtiste/contactDemand";
import ArtistBank from "../../../components/Main/auditorAndArtiste/artistBank";
import ArtistWallet from "../../../components/Main/auditorAndArtiste/artistWallet";
import Subscriptions from "../../../components/Main/auditorAndArtiste/subscriptions";
import Service from "../../../components/Main/services/service";
import SendEmailDialog from "../../adminEmail/SendEmailDialog";
import { UserHeaderBar, UserStatRow, ValidationCodes, IdentitySection, ConditionsSection, ManageList } from "./userInspectorSections";

const DRAWER_TITLES = {
    contacts: "Prises de contact", demands: "Demandes reçues", bank: "Coordonnées bancaires",
    wallet: "Portefeuille", subscriptions: "Abonnements", service: "Fiche KantoBiz",
};

/**
 * Resolves a currency symbol from the Redux countries list.
 * @param {Array} countries - Redux countries list
 * @param {string} code - Currency code from the profile
 * @returns {string} Symbol, the raw code as fallback, or empty string
 */
function resolveCurrencySymbol(countries, code) {
    if (!code) return "";
    const match = (countries || []).find((c) => c.currencies?.code?.toLowerCase() === code.toLowerCase());
    return match ? match.currencies.symbol : code;
}

/**
 * Fetches/caches the country data for the service's country (for the Service drawer).
 * @param {Object} service - Service with a country field
 * @param {Array} countries - Redux countries list
 * @param {Function} dispatch - Redux dispatch
 * @param {Function} setServiceCountry - Resolved-country setter
 * @returns {Promise<void>}
 */
async function checkCountry(service, countries, dispatch, setServiceCountry) {
    const country = service?.country;
    const found = (countries || []).find((tmp) =>
        country?.toLowerCase() === tmp.name.toLowerCase() || country?.toLowerCase() === tmp.nativeName.toLowerCase());
    if (!found) {
        const { data } = await axios.get(`flags/search/country/${country}`);
        setServiceCountry(data);
        await dispatch(addCountries([data, ...countries]));
    } else {
        setServiceCountry(found);
    }
}

/**
 * Toggles a user's ban status; the server notifies the user on ban.
 * @param {number} userId - Target user id
 * @param {string} name - User name for the toast
 * @param {Function} setBanOpen - Closes the confirm dialog
 * @param {string} [reason] - Mandatory reason on ban
 * @returns {Promise<void>}
 */
async function switchBan(userId, name, setBanOpen, reason) {
    try {
        await axios.put(`/admin/users/ban_user_switch/${userId}`, reason ? { reason } : {});
        setBanOpen(false);
        toast.success(`Statut de ${name} modifié. L'utilisateur a été notifié.`);
    } catch (error) {
        toast.error("Erreur lors du changement de statut");
    }
}

/**
 * Renders the full management component for the open drawer.
 * @param {string|null} key - Active drawer key
 * @param {Object} ctx - { data, adminRight, service, serviceCountry, setService }
 * @returns {React.ReactElement|null}
 */
function renderManaged(key, ctx) {
    const { data, adminRight, service, serviceCountry, setService } = ctx;
    if (key === "contacts") return <BookingContactList contactCards={data.artist_contacted} />;
    if (key === "demands") return <ContactDemand contactCards={data.auditor_contacts} />;
    if (key === "bank") return <ArtistBank bank={data.banking} />;
    if (key === "wallet") return <ArtistWallet wallets={data.wallet} currencyCode={data.my_profile?.currency} userId={data.user_id} adminRight={adminRight} />;
    if (key === "subscriptions") return <Subscriptions userId={data.user_id} />;
    if (key === "service") return <Service service={service} serviceCountry={serviceCountry} updateServiceInList={setService} />;
    return null;
}

/**
 * Light, compact user inspector: sticky header + scrollable body with identity,
 * conditions and stat rows, plus drill-down rows that open the existing
 * management components in a focused drawer (so the inline panel stays simple).
 * @param {Object} props
 * @param {Object} props.data - Full user payload
 * @param {number} props.adminRight - Connected admin's right level
 * @returns {React.ReactElement}
 */
function UserInspector({ data, adminRight }) {
    const dispatch = useDispatch();
    const countries = useSelector((s) => s.global.countries);
    const [banOpen, setBanOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [drawerKey, setDrawerKey] = useState(null);
    const [service, setService] = useState(data?.user_services?.[0]);
    const [serviceCountry, setServiceCountry] = useState(undefined);

    useEffect(() => {
        setService(data?.user_services?.[0]);
        setServiceCountry(undefined);
        setDrawerKey(null);
        setBanOpen(false);
        setEmailOpen(false);
    }, [data]);

    useEffect(() => {
        if (drawerKey === "service" && service?.country && !serviceCountry) {
            checkCountry(service, countries, dispatch, setServiceCountry);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [drawerKey, service?.country, serviceCountry]);

    const profile = data.my_profile || {};
    const isAuditor = data.role === "professional_auditor";
    const currency = resolveCurrencySymbol(countries, profile.currency);
    const header = (
        <UserHeaderBar profile={profile} role={data.role} banned={data.banned}
            onBan={() => setBanOpen(true)} onEmail={() => setEmailOpen(true)} />
    );

    return (
        <>
            <InspectorShell header={header}>
                <ValidationCodes keyActivate={data.key_activation} keyPasswordReset={data.key_password_reset} />
                <UserStatRow data={data} />
                <IdentitySection profile={profile} banned={data.banned} />
                {!isAuditor && <ConditionsSection condition={data.conditions} currency={currency} />}
                <ManageList data={data} isAuditor={isAuditor} onOpen={setDrawerKey} />
            </InspectorShell>
            <BanConfirmDialog showModal={banOpen} setShowModal={setBanOpen} banned={data.banned}
                onConfirm={(reason) => switchBan(data.user_id, profile.name, setBanOpen, reason)} />
            <SendEmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} targetType="user"
                targetId={data.user_id} recipientLabel={profile.email} />
            <ManageDrawer isOpen={drawerKey !== null} onClose={() => setDrawerKey(null)} title={DRAWER_TITLES[drawerKey] || ""}>
                {renderManaged(drawerKey, { data, adminRight, service, serviceCountry, setService })}
            </ManageDrawer>
        </>
    );
}

export default UserInspector;
